import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  /** If true, show inline fallback instead of full-screen */
  inline?: boolean;
  /** Section name for error reporting */
  section?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack, section: this.props.section } });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.inline) {
        return (
          <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-700">
            {this.props.section ? `${this.props.section} failed to load` : 'This section failed to load'} —{' '}
            <button onClick={() => this.setState({ hasError: false, error: null })} className="underline font-medium">
              retry
            </button>
          </div>
        );
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-arete-light p-6">
          <div className="card max-w-md w-full text-center space-y-4">
            <h2 className="text-xl font-bold text-arete-navy">Something went wrong</h2>
            <p className="text-sm text-arete-muted">
              An unexpected error occurred. This has been logged for debugging.
            </p>
            {this.state.error && (
              <p className="text-xs text-red-500 bg-red-50 rounded-xl px-4 py-2 font-mono break-all">
                {this.state.error.message}
              </p>
            )}
            <button
              onClick={() => window.location.reload()}
              className="btn-primary inline-block text-sm"
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
